/**
 * OverviewStep - Manager-specific pre-shift step
 * Shows operational overview including staff, reservations, and alerts
 */

import {
  Check,
  Users,
  Clock,
  AlertTriangle,
  Calendar,
  TrendingUp,
  UserCheck,
  Package,
} from 'lucide-react';
import clsx from 'clsx';

interface StaffMember {
  id: string;
  name: string;
  role: string;
  clockedIn: boolean;
  scheduledTime?: string;
}

interface OverviewStepProps {
  staffOnClock: StaffMember[];
  staffScheduled: StaffMember[];
  totalReservations: number;
  expectedCovers: number;
  lowStockAlerts: number;
  eightySixCount: number;
  isReviewed: boolean;
  onAcknowledge: () => void;
}

export default function OverviewStep({
  staffOnClock,
  staffScheduled,
  totalReservations,
  expectedCovers,
  lowStockAlerts,
  eightySixCount,
  isReviewed,
  onAcknowledge,
}: OverviewStepProps) {
  const notClockedIn = staffScheduled.filter(
    (s) => !staffOnClock.some((oc) => oc.id === s.id)
  );

  return (
    <div>
      <h2 className="text-xl font-bold text-slate-900 mb-1">Manager Overview</h2>
      <p className="text-slate-500 mb-6">Review today's operations before your shift</p>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="bg-primary-50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-5 h-5 text-primary-600" />
            <span className="text-sm text-primary-700">Reservations</span>
          </div>
          <p className="text-2xl font-bold text-primary-900">{totalReservations}</p>
        </div>
        <div className="bg-emerald-50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            <span className="text-sm text-emerald-700">Expected Covers</span>
          </div>
          <p className="text-2xl font-bold text-emerald-900">{expectedCovers}</p>
        </div>
        <div className="bg-amber-50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Package className="w-5 h-5 text-amber-600" />
            <span className="text-sm text-amber-700">Low Stock</span>
          </div>
          <p className="text-2xl font-bold text-amber-900">{lowStockAlerts}</p>
        </div>
        <div className="bg-red-50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <span className="text-sm text-red-700">86'd Items</span>
          </div>
          <p className="text-2xl font-bold text-red-900">{eightySixCount}</p>
        </div>
      </div>

      {/* Staff Status */}
      <div className="mb-6">
        <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <Users className="w-4 h-4 text-slate-500" />
          Staff Status
        </h3>

        {/* Currently Clocked In */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-3">
          <div className="flex items-center gap-2 mb-3">
            <UserCheck className="w-4 h-4 text-emerald-600" />
            <span className="font-medium text-emerald-800">
              On Clock ({staffOnClock.length})
            </span>
          </div>
          {staffOnClock.length > 0 ? (
            <div className="space-y-2">
              {staffOnClock.map((staff) => (
                <div key={staff.id} className="flex items-center justify-between text-sm">
                  <span className="text-emerald-900">{staff.name}</span>
                  <span className="text-emerald-600 capitalize">{staff.role}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-emerald-600">No staff clocked in yet</p>
          )}
        </div>

        {/* Not Yet Clocked In */}
        {notClockedIn.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-amber-600" />
              <span className="font-medium text-amber-800">
                Expected ({notClockedIn.length})
              </span>
            </div>
            <div className="space-y-2">
              {notClockedIn.map((staff) => (
                <div key={staff.id} className="flex items-center justify-between text-sm">
                  <span className="text-amber-900">{staff.name}</span>
                  <div className="text-right">
                    <span className="text-amber-600 capitalize">{staff.role}</span>
                    {staff.scheduledTime && (
                      <span className="text-amber-500 ml-2">@ {staff.scheduledTime}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Alerts Section */}
      {(lowStockAlerts > 0 || eightySixCount > 0) && (
        <div className="mb-6">
          <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            Action Items
          </h3>
          <div className="bg-slate-50 rounded-xl p-4 space-y-2">
            {lowStockAlerts > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-slate-700">Review low stock items</span>
                <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                  {lowStockAlerts} items
                </span>
              </div>
            )}
            {eightySixCount > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-slate-700">Confirm 86'd items with kitchen</span>
                <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                  {eightySixCount} items
                </span>
              </div>
            )}
            {notClockedIn.length > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-slate-700">Staff not yet clocked in</span>
                <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                  {notClockedIn.length} staff
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Manager Reminders */}
      <div className="bg-slate-100 rounded-xl p-4 mb-6">
        <h4 className="font-semibold text-slate-700 mb-2">Shift Priorities</h4>
        <ul className="text-sm text-slate-600 space-y-1">
          <li>• Walk the floor and check all sections</li>
          <li>• Confirm kitchen prep levels</li>
          <li>• Review VIP reservations and special requests</li>
          <li>• Check staff section assignments</li>
          <li>• Brief team on any specials or promotions</li>
        </ul>
      </div>

      {/* Acknowledge Button */}
      <button
        onClick={onAcknowledge}
        className={clsx(
          'w-full py-4 rounded-xl font-semibold text-lg transition-all',
          isReviewed
            ? 'bg-emerald-500 text-white'
            : 'bg-primary-600 text-white hover:bg-primary-700'
        )}
      >
        {isReviewed ? (
          <span className="flex items-center justify-center gap-2">
            <Check className="w-5 h-5" />
            Overview Reviewed
          </span>
        ) : (
          "I've Reviewed the Overview"
        )}
      </button>
    </div>
  );
}
